import type { Camera } from '../engine/Camera';
import { GRID, WORLD } from '../constants';

/**
 * Background renderer: radial nebula gradient + world grid + map boundary glow.
 *
 * Performance:
 * - Only iterates gridlines inside the visible AABB (not the full 6000x6000).
 * - All draw calls batched into 2 strokes (minor + major) to minimize state changes.
 * - No allocations per frame after construction.
 */
export class Grid {
  /** Cached background gradient — rebuilt only when viewport resizes. */
  private bgGradient: CanvasGradient | null = null;
  private bgKey = '';

  drawBackground(
    ctx: CanvasRenderingContext2D,
    vw: number,
    vh: number,
    playerHue: number = 200,
    lightMode: boolean = false,
  ): void {
    const key = `${vw}x${vh}:${lightMode ? 'l' : 'd'}`;
    if (key !== this.bgKey || !this.bgGradient) {
      const cx = vw * 0.5;
      const cy = vh * 0.5;
      const r = Math.hypot(cx, cy);
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
      if (lightMode) {
        // Light theme — off-white center fading to a slightly cooler
        // gray at the edges. Keeps the radial-vignette structure of the
        // dark theme so the world still has visual depth.
        g.addColorStop(0, '#f5f7fb');
        g.addColorStop(1, '#dfe4ed');
      } else {
        g.addColorStop(0, GRID.BG_INNER);
        g.addColorStop(1, GRID.BG_OUTER);
      }
      this.bgGradient = g;
      this.bgKey = key;
    }
    ctx.fillStyle = this.bgGradient;
    ctx.fillRect(0, 0, vw, vh);

    // Player-hue tint — dropped to 3% on light because the bright base
    // already strongly registers any color cast (light surfaces are
    // perceptually more saturated than dark ones at the same alpha).
    const tint = ctx.createRadialGradient(
      vw * 0.5, vh * 0.5, 0,
      vw * 0.5, vh * 0.5, Math.hypot(vw, vh) * 0.55,
    );
    const tintAlpha = lightMode ? 0.03 : 0.055;
    tint.addColorStop(0, `hsla(${playerHue} 100% 50% / ${tintAlpha})`);
    tint.addColorStop(1, `hsla(${playerHue} 100% 50% / 0)`);
    ctx.fillStyle = tint;
    ctx.fillRect(0, 0, vw, vh);
  }

  drawGrid(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    lightMode: boolean = false,
  ): void {
    const bounds = camera.getVisibleBounds();
    const step = GRID.MINOR_STEP;
    const major = GRID.MAJOR_EVERY;

    // Snap to step-aligned start positions.
    const startX = Math.floor(bounds.minX / step) * step;
    const endX = Math.ceil(bounds.maxX / step) * step;
    const startY = Math.floor(bounds.minY / step) * step;
    const endY = Math.ceil(bounds.maxY / step) * step;

    const cy = camera.viewportH * 0.5;
    const cx = camera.viewportW * 0.5;
    const z = camera.zoom;
    const camX = camera.position.x;
    const camY = camera.position.y;

    // Minor pass
    ctx.beginPath();
    for (let x = startX; x <= endX; x += step) {
      if (Math.round(x / step) % major === 0) continue;
      const sx = (x - camX) * z + cx;
      ctx.moveTo(sx, 0);
      ctx.lineTo(sx, camera.viewportH);
    }
    for (let y = startY; y <= endY; y += step) {
      if (Math.round(y / step) % major === 0) continue;
      const sy = (y - camY) * z + cy;
      ctx.moveTo(0, sy);
      ctx.lineTo(camera.viewportW, sy);
    }
    ctx.lineWidth = 1;
    // Light theme: switch grid lines to a faint dark gray so they read
    // against the off-white background. Dark theme keeps the neon-blue
    // grid that's been tuned for years.
    ctx.strokeStyle = lightMode ? 'rgba(26, 31, 43, 0.07)' : GRID.MINOR_COLOR;
    ctx.stroke();

    // Major pass
    ctx.beginPath();
    for (let x = startX; x <= endX; x += step) {
      if (Math.round(x / step) % major !== 0) continue;
      const sx = (x - camX) * z + cx;
      ctx.moveTo(sx, 0);
      ctx.lineTo(sx, camera.viewportH);
    }
    for (let y = startY; y <= endY; y += step) {
      if (Math.round(y / step) % major !== 0) continue;
      const sy = (y - camY) * z + cy;
      ctx.moveTo(0, sy);
      ctx.lineTo(camera.viewportW, sy);
    }
    ctx.lineWidth = 1;
    ctx.strokeStyle = lightMode ? 'rgba(26, 31, 43, 0.14)' : GRID.MAJOR_COLOR;
    ctx.stroke();
  }

  /**
   * Cinematic vignette — darkens the edges of the screen.
   *
   * Drawn LAST (above gameplay layers but below the UI overlay) so the world
   * edges fade out toward the rim of the viewport. Gives an instant "film
   * look" upgrade to the whole scene.
   *
   * The gradient is rebuilt only on viewport resize.
   */
  private vignetteGradient: CanvasGradient | null = null;
  private vignetteKey = '';

  drawVignette(ctx: CanvasRenderingContext2D, vw: number, vh: number): void {
    const key = `${vw}x${vh}`;
    if (key !== this.vignetteKey || !this.vignetteGradient) {
      const cx = vw * 0.5;
      const cy = vh * 0.5;
      const inner = Math.min(vw, vh) * 0.42;
      const outer = Math.hypot(cx, cy);
      const g = ctx.createRadialGradient(cx, cy, inner, cx, cy, outer);
      g.addColorStop(0, 'rgba(0,0,0,0)');
      g.addColorStop(0.7, 'rgba(0,0,0,0.28)');
      g.addColorStop(1, 'rgba(0,0,0,0.65)');
      this.vignetteGradient = g;
      this.vignetteKey = key;
    }
    ctx.fillStyle = this.vignetteGradient;
    ctx.fillRect(0, 0, vw, vh);
  }

  drawWorldBorder(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    playerX: number = WORLD.SIZE / 2,
    playerY: number = WORLD.SIZE / 2,
    time: number = 0,
    playerHue: number = 200,
  ): void {
    const z = camera.zoom;
    const cx = camera.viewportW * 0.5;
    const cy = camera.viewportH * 0.5;
    const x = (0 - camera.position.x) * z + cx;
    const y = (0 - camera.position.y) * z + cy;
    const w = WORLD.SIZE * z;

    ctx.save();
    ctx.lineWidth = 2;
    ctx.strokeStyle = GRID.BORDER_COLOR;
    ctx.shadowColor = GRID.BORDER_COLOR;
    ctx.shadowBlur = 18;
    ctx.strokeRect(x, y, w, w);

    // ---- Proximity pulse on the nearest edge ----
    // When the player is within PROXIMITY_RANGE of any edge, draw an animated
    // bright bar along that edge in the player's hue. Gets stronger as the
    // player approaches the wall — communicates "you're against the boundary"
    // without ever needing words.
    const PROXIMITY_RANGE = 500; // world units
    const distLeft   = playerX;
    const distRight  = WORLD.SIZE - playerX;
    const distTop    = playerY;
    const distBottom = WORLD.SIZE - playerY;
    const proximityEdges: Array<[number, 'L' | 'R' | 'T' | 'B']> = [
      [distLeft,   'L'], [distRight, 'R'],
      [distTop,    'T'], [distBottom,'B'],
    ];
    for (const [d, edge] of proximityEdges) {
      if (d > PROXIMITY_RANGE) continue;
      const t = 1 - d / PROXIMITY_RANGE;          // 0 → 1 as player closes in
      const pulse = 0.5 + 0.5 * Math.sin(time * 4.5);
      const alpha = Math.min(1, t * (0.55 + 0.45 * pulse));
      ctx.strokeStyle = `hsla(${playerHue} 100% 65% / ${alpha})`;
      ctx.shadowColor = `hsl(${playerHue} 100% 60%)`;
      ctx.shadowBlur = 22 * t;
      ctx.lineWidth = 2 + 4 * t;
      ctx.beginPath();
      if (edge === 'L') {
        ctx.moveTo(x, y);
        ctx.lineTo(x, y + w);
      } else if (edge === 'R') {
        ctx.moveTo(x + w, y);
        ctx.lineTo(x + w, y + w);
      } else if (edge === 'T') {
        ctx.moveTo(x, y);
        ctx.lineTo(x + w, y);
      } else {
        ctx.moveTo(x, y + w);
        ctx.lineTo(x + w, y + w);
      }
      ctx.stroke();
    }
    ctx.restore();
  }
}
